'use client'

import { useState } from 'react'
import { ColumnDef } from "@tanstack/react-table"
import { ArrowUpDown, MoreHorizontal, Trash, Edit2, Loader2, FileText, CheckCircle, XCircle, AlertCircle } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from '@/components/ui/badge'
import FormModal from "../../components/FormModal"
import UpdateQuotationForm from "./edit/DocumentForm"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import QuotationService from '@/api/quotations.service'
import { useToast } from "@/hooks/use-toast"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

export type QuotationType = {
  id: number
  quotationNumber: string
  amount: number
  currency: string
  status: string
  issueDate: string
  expiryDate: string
  dateUpdated: string
  dateCreated: string
}

export type QuotationItemType = {
  id: number
  quotationId: number
  productServiceId: number
  description: string
  quantity: number
  unitPrice: number
  totalPrice: number
}

export type QuotationTaxType = {
  id: number
  quotationId: number
  taxName: string
  taxRate: number
  taxAmount: number
}

export const columns: ColumnDef<QuotationType>[] = [
  {
    accessorKey: "quotationNumber",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="font-medium text-slate-800 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white whitespace-nowrap"
      >
        Quotation
        <ArrowUpDown className="w-4 h-4 ml-2 opacity-70" />
      </Button>
    ),
    cell: ({ cell }) => (
      <div className="flex items-center">
        <FileText className="w-4 h-4 mr-2 text-slate-400" />
        <span className="font-medium text-slate-900 dark:text-slate-100">
          {cell.row.original.quotationNumber}
        </span>
      </div>
    ),
  },
  {
    accessorKey: "amount",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="font-medium text-slate-800 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white"
      >
        Amount
        <ArrowUpDown className="w-4 h-4 ml-2 opacity-70" />
      </Button>
    ),
    cell: ({ cell }) => (
      <div className="font-medium text-slate-900 dark:text-slate-100">
        {cell.row.original.currency} {cell.row.original.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </div>
    ),
  },
  {
    accessorKey: "status",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="font-medium text-slate-800 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white"
      >
        Status
        <ArrowUpDown className="w-4 h-4 ml-2 opacity-70" />
      </Button>
    ),
    cell: ({ cell }) => {
      const status = cell.getValue() as string;
      
      const statusConfig = {
        accepted: {
          label: "Accepted",
          icon: CheckCircle,
          className: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/40"
        },
        rejected: {
          label: "Rejected",
          icon: XCircle,
          className: "bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400 hover:bg-rose-100 dark:hover:bg-rose-900/40"
        },
        pending: {
          label: "Pending",
          icon: AlertCircle,
          className: "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/40"
        }
      };
      
      const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
      const Icon = config.icon;
      
      return (
        <Badge variant="outline" className={`font-medium flex items-center gap-1 px-2.5 py-0.5 rounded-full ${config.className}`}>
          <Icon className="w-3.5 h-3.5" />
          <span>{config.label}</span>
        </Badge>
      );
    },
  },
  {
    accessorKey: "dateCreated",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="font-medium text-slate-800 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white whitespace-nowrap"
      >
        Created On
        <ArrowUpDown className="w-4 h-4 ml-2 opacity-70" />
      </Button>
    ),
    cell: ({ cell }) => {
      const date = new Date(cell.getValue() as string);
      const formattedDate = `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
      const formattedTime = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
      
      return (
        <div className="text-slate-700 dark:text-slate-300">
          <div className="font-medium">{formattedDate}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400">{formattedTime}</div>
        </div>
      );
    },
  },
  {
    accessorKey: "dates",
    header: "Timeline",
    cell: ({ row }) => {
      const issueDate = row.original.issueDate;
      const expiryDate = row.original.expiryDate;
      
      const isExpired = new Date(expiryDate) < new Date();
      
      return (
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-slate-500 dark:text-slate-400 w-14">Issued:</span>
            <span className="text-sm text-slate-700 dark:text-slate-300">{issueDate}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-slate-500 dark:text-slate-400 w-14">Expires:</span>
            <span className={`text-sm ${isExpired ? "text-rose-600 dark:text-rose-400" : "text-slate-700 dark:text-slate-300"}`}>
              {expiryDate}
            </span>
          </div>
        </div>
      );
    },
  },
  {
    id: "actions",
    cell: ({ row }) => {
      const { toast } = useToast();
      const [isModalOpen, setModalOpen] = useState(false);
      const [isMarkDialogOpen, setMarkDialogOpen] = useState(false);
      const [isMarking, setIsMarking] = useState(false);
      const [markAction, setMarkAction] = useState<'accepted' | 'rejected' | null>(null);
      
      const handleOpenQuotation = () => {
        window.open(`/quotation/${row.original.id}`, '_blank');
      };
      
      const handleMarkStatus = async (status: 'accepted' | 'rejected') => {
        try {
          setIsMarking(true);
          const response = await QuotationService.updateSpecificQuotation(row.original.id, {
            status: status,
          });
          
          if (response.data.result === "success") {
            toast({
              title: status === 'accepted' ? "Quotation Accepted" : "Quotation Rejected",
              description: `Quotation ${row.original.quotationNumber} has been marked as ${status}.`,
              variant: "default",
            });
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          } else {
            toast({
              title: "Error",
              description: response.data.message,
              variant: "destructive",
            });
          }
        } catch (error) {
          toast({
            title: "Error",
            description: `Failed to mark as ${status}.`,
            variant: "destructive",
          });
        } finally {
          setIsMarking(false);
          setMarkDialogOpen(false);
          setMarkAction(null);
        }
      };
      
      const handleDelete = async () => {
        try {
          const response = await QuotationService.deleteQuotation(row.original.id);
          toast({ 
            title: "Quotation Deleted",
            description: "Quotation deleted successfully",
            variant: "default"
          });
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        } catch (error) {
          toast({ 
            title: "Error",
            description: "Failed to delete quotation", 
            variant: "destructive" 
          });
        }
      };
      
      const openMarkDialog = (action: 'accepted' | 'rejected') => {
        setMarkAction(action);
        setMarkDialogOpen(true);
      };

      return (
        <>
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleOpenQuotation}
              className="h-8 px-2 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <FileText className="h-3.5 w-3.5" />
              <span className="sr-only">View</span>
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 data-[state=open]:bg-slate-100 dark:data-[state=open]:bg-slate-800"
                >
                  <MoreHorizontal className="h-4 w-4" />
                  <span className="sr-only">Open menu</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[180px]">
                <DropdownMenuLabel className="text-xs font-normal text-slate-500 dark:text-slate-400">
                  Actions
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {row.original.status === 'pending' && (
                  <>
                    <DropdownMenuItem 
                      onClick={() => openMarkDialog('accepted')}
                      className="text-emerald-600 dark:text-emerald-400 focus:text-emerald-700 dark:focus:text-emerald-300"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Mark as Accepted
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => openMarkDialog('rejected')}
                      className="text-rose-600 dark:text-rose-400 focus:text-rose-700 dark:focus:text-rose-300"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Mark as Rejected
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                {(row.original.status === 'pending' || row.original.status === 'rejected') && (
                  <>
                    <DropdownMenuItem onSelect={() => setModalOpen(true)}>
                      <Edit2 className="w-4 h-4 mr-2" />
                      Edit Quotation
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onSelect={handleDelete}
                      className="text-rose-600 dark:text-rose-400 focus:text-rose-700 dark:focus:text-rose-300"
                    >
                      <Trash className="w-4 h-4 mr-2" />
                      Delete Quotation
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          
          <FormModal
            open={isModalOpen}
            setOpen={setModalOpen}
            title="Edit Quotation"
          >
            <UpdateQuotationForm quotationId={row.original.id.toString()} />
          </FormModal>
          
          <Dialog open={isMarkDialogOpen} onOpenChange={setMarkDialogOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>
                  {markAction === 'accepted' ? 'Accept Quotation' : 'Reject Quotation'}
                </DialogTitle>
                <DialogDescription>
                  Are you sure you want to mark quotation {row.original.quotationNumber} as {markAction}?
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="flex space-x-2 sm:justify-end">
                <Button
                  variant="outline"
                  onClick={() => setMarkDialogOpen(false)}
                  disabled={isMarking}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => markAction && handleMarkStatus(markAction)}
                  disabled={isMarking}
                  className={markAction === 'accepted' 
                    ? "bg-emerald-600 hover:bg-emerald-700 text-white" 
                    : "bg-rose-600 hover:bg-rose-700 text-white"
                  }
                >
                  {isMarking && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {markAction === 'accepted' ? 'Accept' : 'Reject'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      );
    },
  },
]
